import {
  WhmBox,
  WhmCard,
  WhmCardContent,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { useContext, useMemo, useState } from "react";
import { FundHolding } from "../../data/dtos/fund-holdings";
import { FundDetailsModal } from "./FundDetailsModal";
import { FundHoldingSchema } from "../../data/dtos/fund-holding-schema";
import { I18nContext } from "../../contexts/I18nContext";
import { valueFormatter } from "../../components/FundHoldingsTable/utils";

export const FundHoldingsCardMobileComponent = ({
  row,
  fieldColumnsSchemas,
}: {
  row: FundHolding;
  fieldColumnsSchemas: FundHoldingSchema;
}) => {
  const { getTranslation } = useContext(I18nContext);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const fields = useMemo(() => {
    const allFields = fieldColumnsSchemas.map((schema) => {
      const rawValue = row[schema.field as keyof FundHolding];
      const formattedValue =
        typeof rawValue === "string" || typeof rawValue === "number"
          ? String(valueFormatter(rawValue, schema.type))
          : rawValue ?? "N/A";

      return {
        title: getTranslation(schema.titleKey),
        value: formattedValue,
        showOnMobileCard: !!schema.showOnMobileCard,
      };
    });

    return {
      allFields,
      cardFields: allFields
        .filter((field) => field.showOnMobileCard)
        .slice(0, 4), // Maximum 4 fields
    };
  }, [fieldColumnsSchemas, row, getTranslation]);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <WhmCard
        onClick={() => handleOpenModal()}
        elevation={0}
        sx={{ border: (theme) => `1px solid ${theme.palette.divider}` }}
      >
        <WhmCardContent>
          <WhmBox
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              rowGap: "12px",
            }}
          >
            {fields.cardFields.map((field, index) => {
              const isRightColumn = index % 2 === 1;
              const isTopRow = index < 2;
              
              return (
                <WhmBox
                  key={field.title}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    textAlign: isRightColumn ? "right" : "left",
                  }}
                >
                  <WhmTypography
                    variant={isTopRow ? "subtitle2" : "body2"}
                    color="text.primary"
                    sx={{
                      textAlign: isRightColumn ? "right" : "left",
                    }}
                  >
                    {field.value}
                  </WhmTypography>
                </WhmBox>
              );
            })}
          </WhmBox>
        </WhmCardContent>
      </WhmCard>
      <FundDetailsModal
        open={openModal}
        onClose={handleCloseModal}
        fundHoldingInfo={fields.allFields}
      />
    </>
  );
};
